import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { GuardCard } from "./GuardCard";
import { TeamFilter } from "./TeamFilter";
import { RoleFilter } from "./RoleFilter";
import { SearchDialog } from "./SearchDialog";
import { Guard, Team } from "../types";
import { UserRole } from "@/pages/users/types";
import { Users } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
interface GuardsListProps {
  guards: Guard[] | undefined;
  teams: Team[] | undefined;
  isLoading: boolean;
  onGuardRemoved?: () => void;
  isAdmin: boolean;
  isClient?: boolean;
  onTeamFilterChange: (teamId: string | null) => void;
  onRoleFilterChange: (role: UserRole | "all") => void;
  selectedRole: UserRole | "all";
  showArchived?: boolean;
}
export const GuardsList = ({
  guards,
  teams,
  isLoading,
  onGuardRemoved,
  isAdmin,
  isClient = false,
  onTeamFilterChange,
  onRoleFilterChange,
  selectedRole,
  showArchived = false,
}: GuardsListProps) => {
  const { t } = useLanguage();
  const [selectedGuard, setSelectedGuard] = useState<Guard | null>(null);
  const handleGuardSelect = (guard: Guard) => {
    setSelectedGuard(guard);
  };
  if (isLoading) {
    return <div className="space-y-4">
        <div className="flex flex-col sm:flex-row lg:flex-row gap-3 sm:gap-4 lg:gap-6 mb-6 p-4 bg-card rounded-lg border">
          <Skeleton className="h-10 w-full sm:w-48 lg:w-56" />
          <Skeleton className="h-10 w-full sm:w-40 lg:w-44" />
          <Skeleton className="h-10 w-full sm:flex-1 lg:w-64" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({
          length: 6
        }).map((_, i) => <Card key={i} className="p-6">
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-[200px]" />
                    <Skeleton className="h-4 w-[150px]" />
                  </div>
                </div>
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            </Card>)}
        </div>
      </div>;
  }
  return <div className="space-y-8">
      {/* Enhanced filter section with glass morphism */}
      <div className="bg-gradient-to-r from-card/90 via-card/70 to-card/50 backdrop-blur-xl border border-border/50 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center">
            <Users className="w-4 h-4 text-white" />
          </div>
          <h2 className="text-lg font-semibold text-foreground">{t('filterSearchPersonnel')}</h2>
        </div>
        
        <div className="flex flex-col sm:flex-row lg:flex-row items-stretch sm:items-center gap-4 sm:gap-4 lg:gap-6">
          {/* Team Filter */}
          <div className="w-full sm:w-48 lg:w-56 flex-shrink-0">
            <label className="text-sm font-medium text-muted-foreground mb-2 block">{t('teamFilter')}</label>
            <TeamFilter teams={teams || []} onFilterChange={onTeamFilterChange} />
          </div>
          
          {/* Role Filter */}
          <div className="w-full sm:w-40 lg:w-44 flex-shrink-0">
            <label className="text-sm font-medium text-muted-foreground mb-2 block">{t('roleFilter')}</label>
            <RoleFilter onFilterChange={onRoleFilterChange} isClientView={isClient && !isAdmin} />
          </div>
          
          {/* Search Dialog */}
          <div className="w-full sm:flex-1 lg:w-64 lg:flex-shrink-0 lg:ml-auto">
            <label className="text-sm font-medium text-muted-foreground mb-2 block">{t('search')}</label>
            <SearchDialog guards={guards || []} onSelectGuard={handleGuardSelect} isLoading={isLoading} onGuardRemoved={onGuardRemoved} />
          </div>
        </div>
      </div>

      {/* Guards grid with enhanced styling */}
      {!guards || guards.length === 0 ? <Card className="relative overflow-hidden bg-gradient-to-br from-card via-card/90 to-muted/30 border-border/50 shadow-lg">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-blue-500/5 pointer-events-none"></div>
          <div className="relative p-12 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-muted to-muted/70 flex items-center justify-center mb-4">
              <Users className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">{t('noPersonnelFound')}</h3>
            <p className="text-muted-foreground">
              {isAdmin ? t('getStartedAddGuard') : t('noGuardsMatchFilters')}
            </p>
          </div>
        </Card> : <div className="relative">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {guards.map((guard, index) => <div key={guard.id} className="animate-fade-in hover-scale" style={{
          animationDelay: `${index * 50}ms`
        }}>
                <GuardCard guard={guard} onGuardRemoved={onGuardRemoved} isAdmin={isAdmin} />
              </div>)}
          </div>
          
          {/* Stats footer */}
          <div className="mt-8 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-muted/50 rounded-full text-sm text-muted-foreground">
              <Users className="w-4 h-4" />
              <span>{t('showing')} {guards.length} {guards.length !== 1 ? t('users') : t('user')}</span>
            </div>
          </div>
        </div>}
    </div>;
};